import { inject, injectable } from "inversify";
import { ApiPage } from "@/core/models/page.model";
import { WaterIndexModel } from "../../models/water-index.model";
import { TableRepo } from "../table.repo";
import { TollAreaRepo } from "./toll-area.repo";
import dayjs from "dayjs";
import { MultipleOpsResult } from "@/core/models/response.model";

interface _WaterIndexGetParams {
    search?: string;
}

export interface ReaderWaterIndexModel {
    id: string;
    waterUserId: string;
    year: number;
    month: number;
    waterMeterNumber?: number | null;
    note?: string | null;
    latitude: number;
    longtitude: number;
    isLargeAmount?: boolean | null;
    isFullRotation?: boolean | null;
    isReverseRotated?: boolean | null;
}

@injectable()
export class WaterIndexRepo extends TableRepo<WaterIndexModel> {
    @inject(TollAreaRepo)
    private tollAreaRepo!: TollAreaRepo;
    
    constructor() {
        super("/api/WaterIndex");
    }

    async getData(index: number, params: _WaterIndexGetParams, pageSize = 10): Promise<ApiPage<WaterIndexModel>|null> {
        const result = await super.getData(index, params, pageSize)
        if (result) {
            const data: WaterIndexModel[] = [];
            const tollAreas = await this.tollAreaRepo.getAll();
            for (const record of result.data) {
                const tollArea = tollAreas.find(area => area.id === record.tollAreaId);
                const populatedRecord: WaterIndexModel = {
                    ...record,
                    tollArea: tollArea
                };
                data.push(populatedRecord);
            }
            result.data = data;
            return result;
        }
        return null;
    }
    async updateIndex(model: WaterIndexModel) {
        try {
            const response = await this.apiRepo.post(`${this.basepath}/${this.updatePath}`, model);
            if (response?.code === '00') {
                return response;
            };
            return response;
        } catch (err) {
            console.log(err);
        }
    }
    async exportExcel(params: object) {
        try {
            const response = await this.apiRepo.getBlob(`${this.basepath}/export-excel`, {
                ...params
            })
            return response;
        } catch (err) {
            console.log(err);
            return null;
        }
    }

    async initAll(month: string, year: string) {
        try {
            const response = await this.apiRepo.post(`${this.basepath}/init-all-water-index`, {}, {year, month});
            if (response?.code === '00') {
                this.changes.next(Date.now());
                return response;
            };
            return response;
        } catch (err) {
            console.log(err);
        }
    }

    async approve(model: WaterIndexModel, isLargeAmount?: boolean | null, isFullRotation?: boolean | null, isReverseRotated?: boolean | null) {
        try {
            const requestBody: ReaderWaterIndexModel = {
                id: model.id,
                waterUserId: model.waterUserId,
                year: model.year,
                month: model.month,
                waterMeterNumber: model.waterMeterNumber,
                note: model.note,
                latitude: model.latitude,
                longtitude: model.longtitude,
                isLargeAmount: isLargeAmount ?? null,
                isFullRotation: isFullRotation ?? null,
                isReverseRotated: isReverseRotated ?? null,
            };
            const response = await this.apiRepo.post(`${this.basepath}/approve`, requestBody);
            if (response?.code === '00') {
                return true
            };
            return response?.errorMessage;
        } catch (err) {
            console.log(err);
        }
    }

    async exportInvoiceMonth(month: string, year: string, tollAreaId?: string, wardId?: string) {
        try {
            const response = await this.apiRepo.post(`${this.basepath}/export-invoice-month`, {}, {
                year,
                month,
                ...(tollAreaId ? { tollAreaId } : {}),
                ...(wardId ? { wardId } : {}),
            });
            if (response?.code === '00') {
                this.changes.next(Date.now());
                return response;
            };
            return response;
        } catch (err) {
            console.log(err);
        }
    }

    async disapproveMany(models: WaterIndexModel[]): Promise<MultipleOpsResult> {
        let success = 0;
        const responses = await Promise.all(models.map(model => this.apiRepo.get(`${this.basepath}/cancel-approve`, {waterIndexId: model.id})));
        responses.forEach(response => {
            if (response?.code === '00') {
                success++;
            }
        })
        if (success > 0) {
            this.changes.next(Date.now());
        }
        return {success, total: models.length};
    }

    async disapproveOne(model: WaterIndexModel) {
        try {
            const response = await this.apiRepo.get(`${this.basepath}/cancel-approve`, {waterIndexId: model.id});
            if (response?.code === '00') {
                return true
            };
            return response?.errorMessage;
        } catch (err) {
            console.log(err);
        }
    }

    async allByWaterUser(waterUserId: string) {
        try {
            const response = await this.apiRepo.get<ApiPage<WaterIndexModel>>(`${this.basepath}/all-by-wateruser`, {waterUserId});
            if (response?.code === '00') {
                return response.result.data
            };
        } catch (err) {
            console.log(err);
        }
        return [];
    }

    async getDetail(waterIndexId: string): Promise<WaterIndexModel | null> {
        try {
            const response = await this.apiRepo.get<WaterIndexModel>(`${this.basepath}/detail`, {id: waterIndexId});
            if (response?.code === '00' || response?.code === 'SuccessResult') {
                return response.result;
            };
        } catch (err) {
            console.log(err);
        }
        return null;
    }

    async getByWaterUserAndPeriod(waterUserId: string, year: number, month: number): Promise<WaterIndexModel | null> {
        try {
            // Sử dụng endpoint allByWaterUser và filter trong frontend (fallback)
            const allData = await this.allByWaterUser(waterUserId);
            const found = allData.find(item => item.year === year && item.month === month);
            return found || null;
        } catch (err) {
            console.log(err);
        }
        return null;
    }
}